import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { usersService } from '../services/usersService'
import { useDataFetch } from '../hooks/useDataFetch'
import { formatDateTime } from '../utils/helpers'

/**
 * Users Page - Enterprise Standard
 * High density user management table with Referral Management.
 */
export default function Users() {
    const { data: users, loading, error, refetch } = useDataFetch(usersService.getAll)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all') // Filters Account Status
    const [selectedUser, setSelectedUser] = useState(null)
    const [suspendingUser, setSuspendingUser] = useState(null)
    const [cancellingDeletion, setCancellingDeletion] = useState(null)

    const pendingDeletionCount = useMemo(() => (users || []).filter(u => u.pendingDeletion).length, [users])

    const filteredUsers = useMemo(() => {
        if (!users) return []
        return users.filter((user) => {
            const matchesSearch =
                user.username.toLowerCase().includes(search.toLowerCase()) ||
                user.email.toLowerCase().includes(search.toLowerCase()) ||
                user.organisation.toLowerCase().includes(search.toLowerCase()) ||
                (user.referralCode && user.referralCode.toLowerCase().includes(search.toLowerCase())) ||
                user.id.toLowerCase().includes(search.toLowerCase())

            const matchesStatus =
                statusFilter === 'all' ? true :
                statusFilter === 'pending-deletion' ? user.pendingDeletion :
                user.accountStatus === statusFilter
            return matchesSearch && matchesStatus
        })
    }, [users, search, statusFilter])

    const handleUpdateUser = async (updatedData) => {
        try {
            await usersService.updateDetails(selectedUser.id, updatedData)
            refetch()
            setSelectedUser(null)
        } catch (err) {
            console.error(err)
            alert('Failed to update user')
        }
    }

    const handleStatusChange = async (user, newStatus) => {
        if (newStatus === 'suspended') {
            setSuspendingUser(user)
            return
        }
        try {
            await usersService.updateDetails(user.id, { accountStatus: newStatus })
            refetch()
        } catch (err) {
            console.error(err)
            alert('Failed to update status')
        }
    }

    const handleSuspendUser = async () => {
        if (!suspendingUser) return
        try {
            await usersService.updateDetails(suspendingUser.id, {
                accountStatus: 'suspended'
            })
            refetch()
            setSuspendingUser(null)
        } catch (err) {
            console.error(err)
            alert('Failed to suspend user')
        }
    }

    const handleCancelDeletion = async () => {
        if (!cancellingDeletion) return
        try {
            await usersService.cancelDeletion(cancellingDeletion.id)
            refetch()
            setCancellingDeletion(null)
        } catch (err) {
            console.error(err)
            alert('Failed to cancel deletion')
        }
    }

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Users Directory</h1>
                <div className="flex items-center gap-3">
                    <button onClick={refetch} className="btn-secondary shadow-sm">
                        Refresh
                    </button>
                </div>
            </div>

            {/* Pending deletion alert banner */}
            {pendingDeletionCount > 0 && (
                <div
                    className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => setStatusFilter('pending-deletion')}
                >
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="text-sm font-semibold text-red-800">
                        {pendingDeletionCount} account{pendingDeletionCount !== 1 ? 's' : ''} pending deletion — click to filter
                    </span>
                    {statusFilter === 'pending-deletion' && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setStatusFilter('all') }}
                            className="ml-auto text-xs text-red-600 underline hover:text-red-800"
                        >
                            Clear filter
                        </button>
                    )}
                </div>
            )}

            {/* Filters Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 p-5 card-admin">
                <div className="sm:col-span-8 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-admin-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    </div>
                    <input
                        type="text"
                        placeholder="Search by username, email, organisation, ID, or referral code..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-3 py-2.5 text-sm font-semibold input-admin bg-gray-50 focus:bg-white transition-colors"
                    />
                </div>
                <div className="sm:col-span-4 relative">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="appearance-none w-full bg-gray-50 pl-4 pr-10 py-2.5 text-sm font-bold text-admin-text border border-admin-border rounded-lg focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none cursor-pointer"
                    >
                        <option value="all">All Account Statuses</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="read-only">Read-only</option>
                        <option value="pending-deletion">Pending Deletion</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-admin-muted">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="p-4 bg-status-error-bg border border-status-error/20 text-status-error text-sm font-bold rounded-lg flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    {error}
                </div>
            )}

            {/* Table */}
            <div className="card-admin min-h-[400px]">
                <div className="w-full overflow-hidden">
                    <table className="table-admin table-fixed">
                        <thead>
                            <tr>
                                <th className="w-[13%] px-3 py-4">Username</th>
                                <th className="w-[17%] px-3 py-4">Email</th>
                                <th className="w-[14%] px-3 py-4">Organisation</th>
                                <th className="w-[12%] px-3 py-4">Account Status</th>
                                <th className="w-[10%] px-3 py-4">Sub. State</th>
                                <th className="w-[10%] px-3 py-4">Referral Code</th>
                                <th className="w-[7%] px-3 py-4">Ref.</th>
                                <th className="w-[9%] px-3 py-4">Deletion</th>
                                <th className="w-[8%] px-3 py-4 text-right">Created</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {loading ? (
                                <tr><td colSpan={9} className="p-12 text-center text-admin-muted font-bold">Loading user data...</td></tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr><td colSpan={9} className="p-12 text-center text-admin-muted font-bold">No users found matching your filters.</td></tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user.id} className={`hover:bg-gray-50 transition-colors group ${user.pendingDeletion ? 'bg-red-50/40' : ''}`}>
                                        <td className="px-3 py-4 font-bold text-brand-navy border-l-4 border-transparent hover:border-brand-navy">
                                            <Link
                                                to={`/user-profile/${user.id}`}
                                                className="block truncate hover:underline focus:outline-none focus:ring-2 focus:ring-brand-navy/30 rounded-sm"
                                                title={user.username}
                                            >
                                                {user.username}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-4 font-semibold text-brand-navy border-l-4 border-transparent hover:border-brand-navy">
                                            <button
                                                onClick={() => setSelectedUser(user)}
                                                className="block w-full truncate hover:underline text-left focus:outline-none"
                                                title={user.email}
                                            >
                                                {user.email}
                                            </button>
                                        </td>
                                        <td className="px-3 py-4 font-medium text-admin-text truncate" title={user.organisation}>{user.organisation}</td>
                                        <td className="px-3 py-4">
                                            <div className="relative w-full max-w-32">
                                                <select
                                                    value={user.accountStatus}
                                                    onChange={(e) => handleStatusChange(user, e.target.value)}
                                                    className={`appearance-none w-full pl-2.5 pr-7 py-1.5 text-xs font-bold rounded-full border cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all ${user.accountStatus === 'active' ? 'bg-status-success-bg text-status-success border-status-success/20 focus:ring-status-success' :
                                                        user.accountStatus === 'suspended' ? 'bg-status-error-bg text-status-error border-status-error/20 focus:ring-status-error' :
                                                            'bg-gray-100 text-admin-muted border-gray-200 focus:ring-gray-400'
                                                        }`}
                                                >
                                                    <option value="active">Active</option>
                                                    <option value="suspended">Suspended</option>
                                                    <option value="read-only">Read-only</option>
                                                </select>
                                                <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${user.accountStatus === 'active' ? 'text-status-success' :
                                                    user.accountStatus === 'suspended' ? 'text-status-error' : 'text-admin-muted'
                                                    }`}>
                                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-4">
                                            <SubscriptionStateBadge status={user.subscriptionState} />
                                        </td>
                                        <td className="px-3 py-4 font-mono text-xs font-bold text-admin-text truncate" title={user.referralCode || 'None'}>
                                            {user.referralCode || 'None'}
                                        </td>
                                        <td className="px-3 py-4">
                                            <ReferralStatusBadge status={user.referralStatus} />
                                        </td>
                                        <td className="px-3 py-4">
                                            {user.pendingDeletion ? (
                                                <DeletionBadge
                                                    scheduledFor={user.deletionScheduledFor}
                                                    onCancel={() => setCancellingDeletion(user)}
                                                />
                                            ) : (
                                                <span className="text-xs text-admin-muted">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-4 text-right text-admin-muted font-medium tabular-nums text-xs leading-5">
                                            {formatDateTime(user.createdAt)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Modal */}
            {
                selectedUser && (
                    <UserEditModal
                        user={selectedUser}
                        onClose={() => setSelectedUser(null)}
                        onSave={handleUpdateUser}
                    />
                )
            }

            {/* Suspend Confirmation Modal */}
            {
                suspendingUser && (
                    <ConfirmSuspendModal
                        user={suspendingUser}
                        onClose={() => setSuspendingUser(null)}
                        onConfirm={handleSuspendUser}
                    />
                )
            }

            {/* Cancel Deletion Confirmation Modal */}
            {
                cancellingDeletion && (
                    <ConfirmCancelDeletionModal
                        user={cancellingDeletion}
                        onClose={() => setCancellingDeletion(null)}
                        onConfirm={handleCancelDeletion}
                    />
                )
            }
        </div >
    )
}

function ConfirmSuspendModal({ user, onClose, onConfirm }) {
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl border border-status-error/20 w-full max-w-sm p-6">
                <div className="flex items-center gap-3 mb-4 text-status-error">
                    <div className="p-2 bg-status-error-bg rounded-full">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-admin-heading">Suspend User?</h3>
                </div>

                <p className="text-sm text-admin-muted mb-2">
                    Are you sure you want to suspend <span className="font-bold text-admin-text">{user.email}</span>?
                </p>
                <p className="text-xs text-admin-muted mb-6">
                    This will immediately block their access to the platform. You can reactivate them later from the table or Manage menu.
                </p>

                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="btn-secondary flex-1"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="btn-danger flex-1"
                    >
                        Confirm Suspend
                    </button>
                </div>
            </div>
        </div>
    )
}

function DeletionBadge({ scheduledFor, onCancel }) {
    const daysLeft = scheduledFor
        ? Math.max(0, Math.ceil((new Date(scheduledFor).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
        : null
    return (
        <div className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6" />
                </svg>
                {daysLeft !== null ? `${daysLeft}d left` : 'Pending'}
            </span>
            <button
                onClick={onCancel}
                className="text-xs text-green-700 underline hover:text-green-900 text-left"
            >
                Cancel
            </button>
        </div>
    )
}

function ConfirmCancelDeletionModal({ user, onClose, onConfirm }) {
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl border border-green-200 w-full max-w-sm p-6">
                <div className="flex items-center gap-3 mb-4 text-green-700">
                    <div className="p-2 bg-green-50 rounded-full">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-admin-heading">Cancel Account Deletion?</h3>
                </div>
                <p className="text-sm text-admin-muted mb-2">
                    Cancel the pending deletion for <span className="font-bold text-admin-text">{user.email}</span>?
                </p>
                <p className="text-xs text-admin-muted mb-6">
                    This will restore the account to normal standing and remove the scheduled deletion.
                </p>
                <div className="flex gap-3">
                    <button onClick={onClose} className="btn-secondary flex-1">Dismiss</button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg text-sm transition-colors"
                    >
                        Yes, Cancel Deletion
                    </button>
                </div>
            </div>
        </div>
    )
}

function UserEditModal({ user, onClose, onSave }) {
    const [accountStatus, setAccountStatus] = useState(user.accountStatus)
    const [subscriptionState, setSubscriptionState] = useState(user.subscriptionState)
    const [referralStatus, setReferralStatus] = useState(user.referralStatus || 'none')
    const [emailVerified, setEmailVerified] = useState(user.emailVerified ?? false)
    const [notes, setNotes] = useState('')

    // Cancelled logic: Default to view-only/restricted access
    useMemo(() => {
        if (subscriptionState === 'cancelled' && accountStatus !== 'read-only' && accountStatus !== 'suspended') {
            setAccountStatus('read-only')
        }
    }, [subscriptionState])

    const handleSubmit = (e) => {
        e.preventDefault()
        onSave({ accountStatus, subscriptionState, referralStatus, emailVerified, notes })
    }

    const hasReferral = !!user.referralCode

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl border border-admin-border w-full max-w-md transform transition-all scale-100">
                <div className="px-6 py-5 border-b border-admin-border flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div>
                        <h3 className="text-lg font-bold">Manage User Account</h3>
                        <p className="text-xs text-admin-muted mt-0.5 font-medium">{user.email}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-admin-muted hover:text-admin-text hover:bg-gray-200 rounded-full transition-colors">
                        <span className="sr-only">Close</span>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-3">
                    {/* Read Only Info Grid */}
                    <div className="grid grid-cols-2 gap-3 pb-0">
                        <div className="p-2.5 bg-gray-50 rounded-lg border border-admin-border">
                            <label className="text-[10px] font-bold text-admin-muted uppercase tracking-wider block mb-0.5">Organisation</label>
                            <p className="text-sm font-bold text-admin-text truncate" title={user.organisation}>{user.organisation}</p>
                        </div>
                        <div className="p-2.5 bg-gray-50 rounded-lg border border-admin-border">
                            <label className="text-[10px] font-bold text-admin-muted uppercase tracking-wider block mb-0.5">Referral Code</label>
                            <div className="flex items-center gap-2">
                                {user.referralCode ? (
                                    <span className="text-sm font-bold text-admin-text truncate">
                                        {user.referralCode}
                                    </span>
                                ) : (
                                    <span className="text-sm text-admin-muted italic">None</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-admin-border pt-1"></div>

                    {/* Actions Group */}
                    <div className="space-y-3">

                        {/* Subscription State */}
                        <div>
                            <label className="label-admin">Subscription State</label>
                            <div className="relative">
                                <select
                                    value={subscriptionState}
                                    onChange={(e) => setSubscriptionState(e.target.value)}
                                    className="select-admin font-bold"
                                >
                                    <option value="trial">Trial</option>
                                    <option value="active">Active</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="expired">Expired</option>
                                </select>
                            </div>
                        </div>

                        {/* Account Status */}
                        <div>
                            <label className="label-admin">Account Status (Access)</label>
                            <div className="relative">
                                <select
                                    value={accountStatus}
                                    onChange={(e) => setAccountStatus(e.target.value)}
                                    className="select-admin font-bold"
                                >
                                    <option value="active">Active</option>
                                    <option value="suspended">Suspended (Blocked)</option>
                                    <option value="read-only">Read-only (Restricted)</option>
                                </select>
                            </div>
                            {subscriptionState === 'cancelled' && accountStatus === 'read-only' && (
                                <p className="text-[10px] text-status-warning font-bold mt-1">
                                    * User cancelled. Defaulting to Read-only access.
                                </p>
                            )}
                        </div>

                        {/* Email Verified */}
                        <div>
                            <label className="label-admin">Email Verified</label>
                            <div className="relative">
                                <select
                                    value={emailVerified ? 'true' : 'false'}
                                    onChange={(e) => setEmailVerified(e.target.value === 'true')}
                                    className="select-admin font-bold"
                                >
                                    <option value="true">Yes — Verified</option>
                                    <option value="false">No — Unverified</option>
                                </select>
                            </div>
                        </div>

                        {/* Referral Status */}
                        {hasReferral && (
                            <div>
                                <label className="label-admin">Referral Code Status</label>
                                <div className="relative">
                                    <select
                                        value={referralStatus}
                                        onChange={(e) => setReferralStatus(e.target.value)}
                                        className="select-admin font-bold"
                                    >
                                        <option value="approved">Approved (Active)</option>
                                        <option value="pending">Pending Review</option>
                                        <option value="suspended">Suspended (Fraud Control)</option>
                                        <option value="none">None</option>
                                    </select>
                                </div>
                            </div>
                        )}

                    </div>

                    <div>
                        <label className="label-admin">Internal Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            placeholder="Add notes..."
                            className="textarea-admin"
                        />
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-admin-border">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary flex-1"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary flex-1"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

function AccountStatusBadge({ status }) {
    const styles = {
        active: 'badge-success',
        suspended: 'badge-error',
        'read-only': 'badge-neutral',
    }
    const style = styles[status] || styles['read-only']

    return (
        <span className={`badge ${style}`}>
            {status}
        </span>
    )
}

function SubscriptionStateBadge({ status }) {
    const styles = {
        active: 'badge-success',
        trial: 'badge-warning',
        cancelled: 'badge-neutral',
        expired: 'badge-neutral',
    }
    const style = styles[status] || styles.cancelled

    return (
        <span className={`badge ${style}`}>
            {status}
        </span>
    )
}

function ReferralStatusBadge({ status }) {
    const styles = {
        approved: 'text-status-success',
        suspended: 'text-status-error',
        pending: 'text-status-warning',
        none: 'text-admin-muted',
    }
    const style = styles[status] || styles.none

    return (
        <span className={`text-[10px] uppercase font-bold tracking-wider ${style}`}>
            {status}
        </span>
    )
}
